// Throttle leading+trailing genérico e puro (sem dependência de Redux/DOM além
// de setTimeout). Usado pra agrupar rajadas de invalidação de tag RTK Query numa
// janela fixa — ex.: eventos WebSocket do inbox que hoje disparariam um refetch
// por evento (10-50/s × N dashboards). Casando a janela com o TTL do micro-cache
// do backend, cada aba faz no máximo 1 refetch por janela em vez de uma rajada.
//
// Semântica: a primeira chamada com a janela fechada dispara na hora (leading);
// chamadas subsequentes dentro da janela agendam UMA execução no fim dela
// (trailing); ao expirar sem trailing pendente, a janela reabre.

export interface Throttled {
  (): void;
  /** Cancela um trailing agendado e fecha a janela. */
  cancel(): void;
}

export function throttle(fn: () => void, waitMs: number): Throttled {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let trailingPending = false;

  const invoke = (): void => {
    fn();
    // Abre a janela de cooldown; ao expirar dispara o trailing se houve chamada.
    timer = setTimeout(onTimeout, waitMs);
  };

  const onTimeout = (): void => {
    if (trailingPending) {
      trailingPending = false;
      invoke();
    } else {
      timer = null;
    }
  };

  const throttled = (): void => {
    if (timer === null) {
      // Janela fechada → leading imediato.
      invoke();
    } else {
      // Dentro da janela → agenda o único trailing.
      trailingPending = true;
    }
  };

  throttled.cancel = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    trailingPending = false;
  };

  return throttled;
}
